import { useEffect, useState, type ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiFetch, ApiError } from '@/lib/api';
import { clearSession, getToken, type Admin } from '@/lib/auth';

interface MeResponse {
  admin: Admin;
}

type Status = 'checking' | 'ok' | 'unauthed';

export function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const [status, setStatus] = useState<Status>(getToken() ? 'checking' : 'unauthed');
  const location = useLocation();

  useEffect(() => {
    if (!getToken()) {
      setStatus('unauthed');
      return;
    }
    let cancelled = false;
    apiFetch<MeResponse>('/api/auth/me')
      .then(() => {
        if (!cancelled) setStatus('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          clearSession();
        }
        setStatus('unauthed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="flex min-h-full items-center justify-center bg-surface-bg text-fg-muted">
        Checking session…
      </div>
    );
  }
  if (status === 'unauthed') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}
